import { ExternalBlindActiveConfiguration } from '@icc/shutter/types';
import { Component, OnInit, Inject, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { StepComponent, SharedFacade, ModalService } from '@icc/configurator/shared';
import { APP_CONFIG, AppConfigFactory } from '@icc/shared/config';
import { ShutterFacade } from '../+state/shutter.facade';
import { DrivesService } from '../drives.service';
import { map, withLatestFrom, tap } from 'rxjs/operators';
import { AccessoriesService } from '@icc/construction-elements/configurator';
import { IccAccessory, IccAccessoryAccessory } from '@icc/construction-elements/types';
import { ConfigurationsService } from '@icc/configurator/shared';
import { TranslateService } from '@icc/shared';
import { Subscription, BehaviorSubject, combineLatest } from 'rxjs';
import { iccListItem, UnitConverterService } from '@icc/configurator/ui';
import { DriveSidePageComponent } from '../drive-side-page/drive-side-page.component';
import { crankLengths, mountingLengths } from '@icc/shared/configurators/driveEngineStatic';
import { DriveEngineService } from '../drive-engine.service';
import { TypeInfoHangerComponent } from '../type-info-hanger/type-info-hanger.component';
import { _, core } from "@icc/shared/helpers";

// tslint:disable-next-line:class-name
interface colorListItem extends iccListItem {
    id: 'b' | 'r';
}

@Component({
    selector: 'icc-drives',
    templateUrl: './drives.component.html',
    styleUrls: ['./drives.component.scss'],
    standalone: false
})
export class DrivesComponent extends StepComponent implements OnInit, OnDestroy {
    static stepName = _('ROLLER_SHUTTER_LEGACY|Napęd');
    static stepIcon = {
        ligature: 'touch_app',
    };

    public configurator = 'roller_shutter';
    public stepId = 'rollerdrive';
    public title = _('ROLLER_SHUTTER_LEGACY|Wybierz napęd');

    currency: any = {};
    drive$ = this.shutterFacade.drive$;
    separateDrivesAboveWidthThreshold = this.config().IccConfig.Configurators.roller_shutter.separateDrivesAboveWidthThreshold;
    driveSideRadio = this.config().IccConfig.Configurators.roller_shutter.driveSideRadio;
    selectedConfiguratorType$ = this.shutterFacade.selectedConfiguratorType$;
    engineDriveDescriptionContext = {$implicit: '', engineName: '', engineGroups: [], engineGroupsIndicators: []};
    availableCrankSlopes$ = this.shutterFacade.availableCrankSlopes$;
    selectedCrankSlope: number | string = 45;

    mountingLength : number | string = 1;
    crankLength : number | string = 1;
    crankOffset = 1;
    isBlindDataStatic = this.config().IccConfig.Configurators.external_blind?.staticManualDriveData;
    drivesRadio = this.config().IccConfig.Configurators.roller_shutter.drivesRadio;
    showDriveElectrical = this.drivesService.showDriveElectrical();
    isShutterToSmallForElectrical = this.drivesService.shutterWidthToSmallForElectrical() && !this.showDriveElectrical;
    driveElectricalWidth = this.drivesService.getDriveElectricalMinWidth();
    driveType$ = this.drivesService.driveType$;
    driveOverride$ = this.shutterFacade.driveOverride$;
    staticMountingLengths = mountingLengths
    staticCrankLengths = crankLengths;
    showDriveOverride$ = this.drive$.pipe(
        map(
            drive =>
                (drive.type === 'electrical' || drive.type === 'remote')
                && this.config().IccConfig.Configurators.roller_shutter.overload
        )
    );
    driveElements$ = this.shutterFacade.driveElements$;
    driveManual$ = this.shutterFacade.driveManual$;

    windowAccessoriesManufacturerCode = this.config().IccConfig.Configurators
        .windowAccessoriesManufacturerCode;
    driveManuals$ = new BehaviorSubject([]);
    driveManualType$ = this.driveManual$.pipe(
        withLatestFrom(this.driveManuals$),
        map(([driveManual, driveManuals]) => {
            if (driveManuals.length > 0) {
                let manualType = driveManuals[0];
                if (driveManual) {
                    if (driveManual.type && driveManual.type.indexOf('cord_retractor') > -1) {
                        manualType = core.fIdO(driveManuals, 'cord_retractor', 'type');
                    } else if (driveManual.type && driveManual.type.indexOf('retractor') > -1) {
                        manualType = core.fIdO(driveManuals, 'retractor', 'type');
                    } else {
                        manualType = driveManual;
                    }
                }
                return manualType.id;
            }
        })
    );

    retractorColors: colorListItem[] = [
        {
            id: 'r',
            title: this.translateService.instant('COLOR|Biały'),
            color: '#FFFFFF',
        },
        {
            id: 'b',
            title: this.translateService.instant('COLOR|Brązowy'),
            color: '#8a6642',
        },
    ];

    allShuttersCommonRail$ = this.shutterFacade.shutters$.pipe(
        map(shutters => shutters.filter(shutter => shutter.commonRail).length === shutters.length - 1)
    );

    driveSides$ = combineLatest(this.drive$, this.driveManual$, this.shutterFacade.shutters$, this.shutterFacade.selectedType$).pipe(
        map(([drive, driveManual, shutters, type]) => {
            const options: { key: 'l' | 'r' | 'o', value: string}[] = [];
            if (
                type.type === 'Z' && shutters.length === 2 && !shutters[0].commonRail && !shutters[1].commonRail
            ) {
                options.push({ key: 'o', value: this.translateService.instant('ROLLER_SHUTTER_LEGACY|Zewnętrzne') });
            }
            if (
                shutters.length === 1 ||
                (shutters.length === 2 && (shutters.some(shutter => shutter.commonRail) || type.type === 'Z')) ||
                (shutters.length === 3 && shutters.every(shutter => !shutter.commonRail))
            ) {
                options.push({ key: 'l', value: this.translateService.instant('ROLLER_SHUTTER_LEGACY|Lewa') });
                options.push({ key: 'r', value: this.translateService.instant('ROLLER_SHUTTER_LEGACY|Prawa') });
            }
            return options;
        })
    );

    driveSide$ = combineLatest(this.shutterFacade.shutters$, this.shutterFacade.selectedType$).pipe(
        map(([shutters, type]) => {
            if (type.type === 'Z' && shutters.length === 2 && !shutters[0].commonRail && !shutters[1].commonRail && shutters[0].handling === 'l' && shutters[1].handling === 'r') {
                return 'o';
            }
            if (shutters.length === 1 || shutters.length === 2) {
                return shutters[0].handling;
            }
            if (shutters.length === 3 && shutters.every(shutter => !shutter.commonRail)) {
                return shutters[1].handling;
            }
        })
    );

    openingLockAvailable$ = combineLatest(this.drive$, this.driveManual$, this.shutterFacade.selectedConfiguratorType$).pipe(
        map(
            ([drive, driveManual, configuratorType]) =>
                configuratorType === 'roller_shutter'
                && this.config().IccConfig.Configurators.roller_shutter.openingLock
                && this.drivesService.getHangers().length > 0
        )
    );

    openingLockDisable$ = combineLatest(this.drive$, this.driveManual$, this.shutterFacade.hanger$, this.shutterFacade.selectedConfiguratorType$).pipe(
        map(
            ([drive, driveManual, hanger, configuratorType]) =>
                configuratorType === 'roller_shutter'
                && this.config().IccConfig.Configurators.roller_shutter.openingLock
                && (!(this.drivesService.getHangers().find(el => el && el.blockade)
                    && this.drivesService.getHangers().find(el => el && !el.blockade)
                )
                || (hanger.drivesObligatory && hanger.drivesObligatory.includes(drive.id)))
        )
    );

    driveManualTypeColor$ = this.shutterFacade.driveManual$.pipe(
        map(driveManual => (driveManual && driveManual.type.includes('brown') ? 'b' : 'r'))
    );
    openingLock$ = this.shutterFacade.openingLock$;
    driveOutputSide$ = this.shutterFacade.driveOutputSide$;
    driveOutputSides$ = this.drivesService.driveOutputSides$;
    driveOutputSides = {
        'rear-up': {
            image: '/application/dist/web/img/layouts/default/output_rear-up.png',
            title: _('ROLLER_SHUTTER_LEGACY|Tył - góra'),
        },
        'rear-down': {
            image: '/application/dist/web/img/layouts/default/output_rear-down.png',
            title: _('ROLLER_SHUTTER_LEGACY|Tył - dół'),
        },
        'front-up': {
            image: '/application/dist/web/img/layouts/default/output_front-up.png',
            title: _('ROLLER_SHUTTER_LEGACY|Przód - góra'),
        },
        'front-down': {
            image: '/application/dist/web/img/layouts/default/output_front-down.png',
            title: _('ROLLER_SHUTTER_LEGACY|Przód - dół'),
        },
        side: {
            image: '/application/dist/web/img/layouts/default/output_side.png',
            title: _('ROLLER_SHUTTER_LEGACY|Bok'),
        },
        'side-up': {
            image: '/application/dist/web/img/layouts/default/output_side-up.png',
            title: _('ROLLER_SHUTTER_LEGACY|Bok - góra'),
        },
        'side-down': {
            image: '/application/dist/web/img/layouts/default/output_side-down.png',
            title: _('ROLLER_SHUTTER_LEGACY|Bok - dół'),
        }
    }

    dimensionUnit = this.unitConverterService.getUnit();

    private subscriptions: Subscription[] = [];

    constructor(
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private shutterFacade: ShutterFacade,
        private sharedFacade: SharedFacade,
        private drivesService: DrivesService,
        private accessoriesService: AccessoriesService,
        private translateService: TranslateService,
        private modalService: ModalService,
        private configurationsService: ConfigurationsService<'roller_shutter' | 'external_blind' | 'window'>,
        private unitConverterService: UnitConverterService,
        private driveEngineService: DriveEngineService,

    ) {
        super();
    }

    ngOnInit() {
        if (this.configurationsService.conf.Current instanceof ExternalBlindActiveConfiguration) {
            const rollerShutter = this.configurationsService.conf.Current.RollerShutter;
            this.selectedCrankSlope = rollerShutter.crankSlope ? rollerShutter.crankSlope : 45;
            this.crankLength = rollerShutter.crankLength ? rollerShutter.crankLength : 1;
            this.crankOffset =  rollerShutter.crankOffset ? rollerShutter.crankOffset : 1;
            this.mountingLength =  rollerShutter.mountingLength ? rollerShutter.mountingLength : 1;
        }
        if (this.isBlindDataStatic && this.configurationsService.conf.Current.type==='external_blind'){
            const rollerShutter = this.configurationsService.conf.Current.RollerShutter;
            this.mountingLength = this.staticMountingLengths[0];
            this.mountingLength = this.staticMountingLengths.includes(String(rollerShutter.mountingLength)) ? rollerShutter.mountingLength : this.staticMountingLengths[0];
            this.crankLength = this.staticCrankLengths.includes(String(rollerShutter.crankLength)) ? rollerShutter.crankLength : this.staticCrankLengths[0];
            this.selectedCrankSlope = rollerShutter.crankSlope ? rollerShutter.crankSlope : 45;
            this.setCrankLength(this.crankLength);
            this.setMountingLength(this.mountingLength);
        }
        this.subscriptions.push(
            this.sharedFacade.currency$.subscribe(currency => {
                this.currency = currency;
            }),
            this.drive$.subscribe(drive=>{
                this.updateDrive(drive);
            }),
        );

        let hasRetractor = false;
        let hasCordRetractor = false;
        this.driveManuals$.next(
            this.drivesService.driveManuals.reduce((prev, cur) => {
                if (!cur.type || cur.type === 'other') {
                    prev.push(cur);
                } else if (
                    cur.type.indexOf('cord_retractor') > -1
                    && !hasCordRetractor
                ) {
                    prev.push({
                        id: 'cr',
                        price: '0',
                        type: 'cord_retractor',
                        name: this.translateService.instant('ROLLER_SHUTTER_LEGACY|Zwijacz sznurka'),
                    });
                    hasCordRetractor = true;
                } else if (
                    cur.type.indexOf('retractor') > -1
                    && !hasRetractor
                ) {
                    prev.push({
                        id: 'r',
                        price: '0',
                        type: 'retractor',
                        name: this.translateService.instant('ROLLER_SHUTTER_LEGACY|Zwijacz taśmy'),
                    });
                    hasRetractor = true;
                }
                return prev;
            }, [])
        );
    }

    updateDrive(drive){
        const separatedEngines=this.driveEngineService.separateEngines(drive?.driveEngine);
        this.engineDriveDescriptionContext.$implicit=drive?.name;
        this.engineDriveDescriptionContext.engineName=drive?.driveEngine;
        this.engineDriveDescriptionContext.engineGroupsIndicators=this.driveEngineService.separatedEnginesIndicators(separatedEngines);
        this.engineDriveDescriptionContext.engineGroups=separatedEngines || [];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    setMountingLength(mountingLength) {
        this.drivesService.setMountingLength(mountingLength);
    }

    setCrankSlope(slope) {
        this.drivesService.setCrankSlope(slope);
    }

    setCrankLength(crankLength) {
        this.drivesService.setCrankLength(crankLength);
    }

    setCrankOffset(crankOffset) {
        this.drivesService.setCrankOffset(crankOffset);
    }

    openModalDrives() {
        this.drivesService.openModalDrives();
    }

    openModalElectricalDrives() {
        this.drivesService.openModalDrives(true);
    }

    changeDriveType(type: 'manual' | 'electrical') {
        this.drivesService.changeDriveType(type);
    }

    changeOverride(override: 'none' | 'override') {
        this.drivesService.changeOverrideHanger(override);
    }

    checkOverride(override: 'none' | 'override') {
        return this.drivesService.checkOverride(override);
    }

    addAccessoriesToDrive() {
        this.accessoriesService
            .openModalAccessories('drive', undefined, result => {
                this.drivesService.addAccessory(result.accessory);
            })
            .then(() => {});
    }

    remove(accessory: IccAccessoryAccessory) {
        this.drivesService.removeAccessory(accessory);
    }

    openModalDrivesManuals() {
        this.drivesService.openModalDrivesManuals();
    }

    changeDriveManual(manualId: string) {
        this.drivesService.changeDriveManual(manualId);
    }

    changeDriveManualColor(item: colorListItem) {
        this.drivesService.changeDriveManualColor(item.id);
    }

    changeOpeningLock(value: boolean) {
        this.drivesService.setHanger(value);
    }

    changeDriveSide(side: 'l' | 'r' | 'o') {
        this.drivesService.changeDriveSide(side);
    }

    changeDriveSidePage() {
        this.modalService.open({
            pageComponent: DriveSidePageComponent,
        });
    }

    changeDriveOutputSide(side: 'rear-up' | 'rear-down' | 'front-up' | 'front-down' | 'side' | 'side-up' | 'side-down') {
        this.drivesService.changeDriveOutputSide(side);
    }

    getHangerName() {
        if(this.configurationsService.conf.Current.RollerShutter.hanger) {
            return this.configurationsService.conf.Current.RollerShutter.hanger.name;
        }
        return '';
    }

    showInfo() {
        const hanger = this.configurationsService.conf.Current.RollerShutter.hanger;
        const modalService = this.modalService
            .open({
                pageComponent: TypeInfoHangerComponent,
                resolve: {
                    hanger: () => hanger,
                },
            });
      }
}
